import React, { useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { MdCardMembership, MdCheckCircle, MdCancel } from "react-icons/md";

import { getMyPlan } from "../../store/slices/plan";

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRemainingDays = (expiryDate) => {
  if (!expiryDate) return 0;

  const diff =
    new Date(expiryDate).getTime() - new Date().getTime();

  return Math.max(
    0,
    Math.ceil(diff / (1000 * 60 * 60 * 24))
  );
};

const getDuration = (duration, durationType) => {
  if (!duration) return "-";

  const type =
    durationType === "days"
      ? duration === 1
        ? "Day"
        : "Days"
      : durationType === "months"
        ? duration === 1
          ? "Month"
          : "Months"
        : durationType === "year" || durationType === "years"
          ? duration === 1
            ? "Year"
            : "Years"
          : durationType;

  return `${duration} ${type}`;
};

const MyPlan = () => {
  const dispatch = useDispatch();

  const { myPlan, myPlanLoading } = useSelector(
    (state) => state.plan
  );

  useEffect(() => {
    dispatch(getMyPlan());
  }, [dispatch]);

  /* ==============================
     LOADING
  ============================== */

  if (myPlanLoading) {
    return (
      <div className="ep-section mt-5">
        <div className="ep-sec-header">
          <div className="ep-sec-left">
            <div
              className="ep-sec-icon"
              style={{ background: "#fff4e8" }}
            >
              <MdCardMembership />
            </div>

            <div>
              <div className="ep-sec-title">
                My Membership
              </div>

              <div className="ep-sec-subtitle">
                Manage your current membership plan
              </div>
            </div>
          </div>
        </div>

        <div className="my-plan-minimal-loading">
          Loading membership...
        </div>
      </div>
    );
  }

  /* ==============================
     NO PLAN
  ============================== */

  if (!myPlan) {
    return (
      <div className="ep-section mt-5">
        <div className="ep-sec-header">
          <div className="ep-sec-left">
            <div
              className="ep-sec-icon"
              style={{ background: "#fff4e8" }}
            >
              <MdCardMembership />
            </div>

            <div>
              <div className="ep-sec-title">
                My Membership
              </div>

              <div className="ep-sec-subtitle">
                Manage your current membership plan
              </div>
            </div>
          </div>
        </div>

        <div className="my-plan-no-plan">
          <strong>No Active Membership</strong>
          <span>
            You don't currently have an active membership plan.
          </span>
        </div>
      </div>
    );
  }

  /* ==============================
     DATA
  ============================== */

  const plan = myPlan.plan || {};

  const chatsUsed =
    myPlan.usage?.chatsUsed || 0;

  const contactViewsUsed =
    myPlan.usage?.contactViewsUsed || 0;

  const chatLimit =
    plan.limits?.chats ?? 0;

  const contactLimit =
    plan.limits?.contactViews ?? 0;

  const remainingDays = getRemainingDays(
    myPlan.planExpiresAt
  );

  const contactPercentage =
    contactLimit > 0
      ? Math.min(
        100,
        (contactViewsUsed / contactLimit) * 100
      )
      : 0;

  const isActive =
    myPlan.status === "active";

  return (
    <div className="ep-section mt-5">

      {/* ==============================
          HEADER
      ============================== */}

      <div className="ep-sec-header">
        <div className="ep-sec-left">

          <div
            className="ep-sec-icon"
            style={{ background: "#fff4e8" }}
          >
            <MdCardMembership />
          </div>

          <div>
            <div className="ep-sec-title">
              My Membership
            </div>

            <div className="ep-sec-subtitle">
              Manage your current membership plan
            </div>
          </div>

        </div>
      </div>

      <div className="my-plan-minimal">



        <div className="minimal-plan">

          <div>
            <span className="minimal-label">
              CURRENT PLAN
            </span>

            <div className="minimal-plan-name">
              <strong>
                {plan.name || "Membership Plan"}
              </strong>

              <span
                className={
                  isActive
                    ? "minimal-status active"
                    : "minimal-status"
                }
              >
                ● {isActive ? "Active" : myPlan.status}
              </span>
            </div>
          </div>

          <div className="minimal-price">
            {Number(plan.price) === 0
              ? "FREE"
              : `₹${Number(
                plan.price
              ).toLocaleString("en-IN")}`}
          </div>

        </div>


        <div className="minimal-details">


          <div>
            <span>Started</span>
            <strong>
              {formatDate(
                myPlan.planStartsAt
              )}
            </strong>
          </div>

          <div>
            <span>Expires</span>
            <strong>
              {formatDate(
                myPlan.planExpiresAt
              )}
            </strong>
          </div>

          <div>
            <span>Chats</span>
            <strong>
              {chatsUsed}
              <small>
                {chatLimit === -1
                  ? " / Unlimited"
                  : ` / ${chatLimit}`}
              </small>
            </strong>
          </div>

          <div>
            <span>Contacts</span>
            <strong>
              {contactViewsUsed}
              <small>
                {contactLimit === -1
                  ? " / Unlimited"
                  : ` / ${contactLimit}`}
              </small>
            </strong>
          </div>


        </div>


        <div className="minimal-section">

          <h5>Features</h5>

          <div className="minimal-features">

            <span
              className={
                plan.features?.verifiedBadge
                  ? "feature-on"
                  : "feature-off"
              }
            >
              {plan.features?.verifiedBadge ? (
                <MdCheckCircle />
              ) : (
                <MdCancel />
              )}

              Verified Badge
            </span>

            <span
              className={
                plan.features?.profileHighlight
                  ? "feature-on"
                  : "feature-off"
              }
            >
              {plan.features?.profileHighlight ? (
                <MdCheckCircle />
              ) : (
                <MdCancel />
              )}

              Profile Highlight
            </span>

          </div>

        </div>



      </div>
    </div>
  );
};

export default MyPlan;